import { useState, useEffect } from "react";
import { Sparkles, Search, ShieldCheck, BarChart3, Check } from "lucide-react";

interface AIAnalysisOverlayProps {
  isActive: boolean;
}

const analysisSteps = [
  {
    label: "Reviewing your request details",
    icon: Search,
    duration: 1800,
  },
  {
    label: "Checking previous context and similar jobs",
    icon: BarChart3,
    duration: 2200,
  },
  {
    label: "Confirming service coverage and routing",
    icon: ShieldCheck,
    duration: 1600,
  },
  {
    label: "Preparing your summary and next steps",
    icon: Sparkles,
    duration: 2400,
  },
];

export function AIAnalysisOverlay({ isActive }: AIAnalysisOverlayProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    if (!isActive) {
      setCurrentStepIndex(0);
      setCompletedSteps([]);
      return;
    }

    let stepIdx = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    function advanceStep() {
      if (stepIdx < analysisSteps.length - 1) {
        setCompletedSteps((prev) => [...prev, stepIdx]);
        stepIdx++;
        setCurrentStepIndex(stepIdx);
        timers.push(setTimeout(advanceStep, analysisSteps[stepIdx].duration));
      }
      // Last step stays "in progress" until the parent resolves
    }

    timers.push(setTimeout(advanceStep, analysisSteps[0].duration));

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/100 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="w-full max-w-md mx-4 rounded-3xl border border-border bg-background p-6 shadow-2xl">
        {/* Animated brain/sparkle icon */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center animate-pulse">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
            {/* Orbiting dot */}
            <div className="absolute inset-0 animate-spin" style={{ animationDuration: "3s" }}>
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary" />
            </div>
          </div>
        </div>

        <h2 className="text-xl font-bold text-center mb-2 font-heading">
          Reviewing your request
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-8">
          This usually takes a few seconds while we prepare your summary.
        </p>

        {/* Step list */}
        <div className="space-y-3">
          {analysisSteps.map((step, index) => {
            const Icon = step.icon;
            const isCompleted = completedSteps.includes(index);
            const isCurrent = index === currentStepIndex && !isCompleted;
            const isPending = index > currentStepIndex;

            return (
              <div
                key={index}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-500
                  ${
                    isCompleted
                      ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800"
                      : isCurrent
                        ? "bg-card border-primary/30 shadow-sm"
                        : "bg-muted/80 border-border/60 opacity-70"
                  }
                `}
              >
                {/* Icon / check */}
                <div
                  className={`
                    w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-500
                    ${
                      isCompleted
                        ? "bg-green-500/10 text-green-600 dark:text-green-400"
                        : isCurrent
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                    }
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>

                {/* Label */}
                <span
                  className={`
                    text-sm font-medium transition-colors duration-500
                    ${
                      isCompleted
                        ? "text-green-700 dark:text-green-300"
                        : isCurrent
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                  `}
                >
                  {step.label}
                </span>

                {/* Spinner for current */}
                {isCurrent && (
                  <div className="ml-auto">
                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
